/** Applies per-instance branding theme variables to the document root. */

import { useEffect } from "react";
import type { ReactNode } from "react";

export interface ThemeProviderProps {
  /** CSS custom properties from `instance_settings.theme`, or null for defaults. */
  theme: Record<string, string> | null | undefined;
  children: ReactNode;
}

/**
 * Sets each branding CSS variable on `:root`, overriding the defaults in
 * `theme.css`. Only variables prefixed `--` are applied, so a malformed theme
 * value can never inject arbitrary CSS.
 */
export function ThemeProvider({ theme, children }: ThemeProviderProps): ReactNode {
  useEffect(() => {
    if (!theme) return;
    const root = document.documentElement;
    const applied: string[] = [];
    for (const [key, value] of Object.entries(theme)) {
      if (key.startsWith("--")) {
        root.style.setProperty(key, value);
        applied.push(key);
      }
    }
    return () => {
      for (const key of applied) root.style.removeProperty(key);
    };
  }, [theme]);

  return children;
}
